import { useEffect, useState } from "react";
import { LockKeyhole, LogIn, ShieldCheck } from "lucide-react";
import { get, getErrorMessage, post } from "@/lib/api";

export default function AuthGate({ children }) {
  const [state, setState] = useState({ loading: true, authenticated: false, configured: true });
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    try {
      const response = await get("/auth/status");
      setState({ loading: false, ...response.data });
    } catch (requestError) {
      setState({ loading: false, authenticated: false, configured: true });
      setError(getErrorMessage(requestError));
    }
  };

  useEffect(() => {
    const onExpired = () => {
      setState((current) => ({ ...current, loading: false, authenticated: false }));
      setError("Session expired — sign in again");
    };
    window.addEventListener("atlas:auth-expired", onExpired);
    check();
    return () => window.removeEventListener("atlas:auth-expired", onExpired);
  }, []);

  const login = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/auth/login", { token });
      setToken("");
      await check();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return <div className="auth-screen"><div className="auth-card">Checking secure session…</div></div>;
  }
  if (state.authenticated) return children;

  if (!state.configured) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-icon"><LockKeyhole size={20} /></div>
          <p className="eyebrow">ATLAS / CONFIGURATION REQUIRED</p>
          <h1>Control Room is locked</h1>
          <p className="auth-copy">Set API_AUTH_TOKEN in backend/.env and restart the API before opening trading controls.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={login}>
        <div className="auth-icon"><LockKeyhole size={20} /></div>
        <p className="eyebrow">ATLAS / SECURE ACCESS</p>
        <h1>Sign in to Control Room</h1>
        <p className="auth-copy">Your token is exchanged for an HttpOnly session and is never embedded in the frontend bundle.</p>
        <label htmlFor="api-token">API access token</label>
        <input
          id="api-token"
          type="password"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          autoComplete="current-password"
          required
        />
        {error && <p className="auth-error" role="alert">{error}</p>}
        <button className="primary-button auth-submit" disabled={busy}>
          <LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="auth-safe-note"><ShieldCheck size={13} /> Trading controls remain protected by the backend risk engine.</div>
      </form>
    </div>
  );
}
